import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "../api";
import useUrlState from "../hooks/useUrlState";
import AdminSelect from "./AdminSelect";

/**
 * Admin "Integration Tests" tab — form-driven Join/Renew/Re-issue
 * scenarios that exercise the real signup paths against the venue's
 * sandbox credential set. Runner is locked to mode=sandbox; rows
 * created during a run carry is_test=true.
 *
 * Architecture:
 *   - Preflight on mount → credential checklist + scenario list
 *   - Picking a scenario → fetch its prefill (realistic sample)
 *   - Run → POST result envelope rendered in the bottom panel
 *
 * The form is intentionally generic: the prefill object's shape is
 * the contract between scenario and form. Today every scenario
 * returns a flat object plus a `memberData` sub-object; the form
 * renders both with simple key/value text inputs. As scenarios get
 * fancier, replace this with a per-scenario form component.
 */

export default function IntegrationTestsTab({ venueId }) {
  const [preflight, setPreflight] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [scenarioId, setScenarioId] = useUrlState("it", "");
  const [input,    setInput]      = useState(null);
  const [busy,     setBusy]       = useState(false);
  const [result,   setResult]     = useState(null);
  const [err,      setErr]        = useState("");

  const reload = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await adminApi.itPreflight(venueId);
      setPreflight(r);
      if (!scenarioId && r.scenarios?.length) setScenarioId(r.scenarios[0].id);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [venueId, scenarioId]);
  useEffect(() => { reload(); }, [reload]);

  // Pull the prefill whenever the picked scenario changes.
  useEffect(() => {
    if (!scenarioId) return;
    setInput(null); setResult(null);
    adminApi.itPrefill(venueId, scenarioId)
      .then((data) => setInput(data || {}))
      .catch((e) => setErr(e.message));
  }, [venueId, scenarioId]);

  async function run() {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await adminApi.itRun(venueId, scenarioId, input || {});
      setResult(r);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  if (loading) return <div style={{ color: "var(--text-muted)" }}>Loading sandbox preflight…</div>;
  if (!preflight) return <div className="toast toast-error">Preflight failed.</div>;

  const checks = preflight.checks || [];
  const allOk  = checks.every((c) => c.ok);

  return (
    <div className="form-section">
      <div className="section-header-row">
        <div>
          <h2>Integration Tests</h2>
          <p className="section-desc">Run the real Join / Renew / Re-issue flows against this venue's sandbox credentials. Forced sandbox · is_test rows.</p>
        </div>
        <PurgeButton venueId={venueId} onPurged={reload} />
      </div>

      {err && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{err}</div>}

      {/* Sandbox credential checklist */}
      <fieldset className="dc-fieldset" style={{ marginBottom: "1.25rem" }}>
        <legend>Sandbox credentials</legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.5rem" }}>
          {checks.map((c) => (
            <div key={c.service} style={{
              padding: "0.55rem 0.75rem",
              background: c.ok ? "var(--success-soft)" : "var(--warning-soft)",
              border: `1px solid ${c.ok ? "var(--success)" : "var(--warning)"}`,
              borderRadius: 6,
              fontSize: "0.85rem",
            }}>
              <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{c.ok ? "✓" : "✗"} {c.service}</div>
              {!c.ok && c.missing && (
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 2 }}>
                  Missing: {c.missing.map((m) => m.field || m.note || JSON.stringify(m)).join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      </fieldset>

      {/* Scenario picker + form */}
      <fieldset className="dc-fieldset">
        <legend>Scenario</legend>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "1rem" }}>
          <div style={{ flex: 1 }}>
            <AdminSelect
              value={scenarioId}
              onChange={setScenarioId}
              options={preflight.scenarios.map((s) => ({ value: s.id, label: s.name }))}
              placeholder="Pick a scenario"
            />
          </div>
          <button className="btn btn-primary" disabled={busy || !allOk || !scenarioId} onClick={run}>
            {busy ? "Running…" : "Run"}
          </button>
        </div>
        <p className="section-desc" style={{ margin: 0, marginBottom: "0.5rem" }}>
          {preflight.scenarios.find((s) => s.id === scenarioId)?.description}
        </p>

        {input && <InputForm value={input} onChange={setInput} />}
      </fieldset>

      {result && <ResultPanel result={result} />}
    </div>
  );
}

/* Generic prefill form — flat keys + a `memberData` sub-object.
   Each leaf gets a text input. */
function InputForm({ value, onChange }) {
  const setField = (key, v) => onChange({ ...value, [key]: v });
  const setMember = (k, v) => onChange({ ...value, memberData: { ...(value.memberData || {}), [k]: v } });

  const flatKeys = Object.keys(value).filter((k) => k !== "memberData");
  const memberKeys = value.memberData ? Object.keys(value.memberData) : [];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.7rem", marginBottom: "1rem" }}>
        {flatKeys.map((k) => (
          <div className="form-field" key={k}>
            <label>{k}</label>
            <input value={value[k] ?? ""} onChange={(e) => setField(k, e.target.value)} />
          </div>
        ))}
      </div>
      {memberKeys.length > 0 && (
        <>
          <div className="form-section-label">Member details</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.7rem" }}>
            {memberKeys.map((k) => (
              <div className="form-field" key={k}>
                <label>{k}</label>
                <input value={value.memberData[k] ?? ""} onChange={(e) => setMember(k, e.target.value)} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* Renders { ok, summary, error, trace } — matches runner envelope. */
function ResultPanel({ result }) {
  return (
    <fieldset className="dc-fieldset" style={{ marginTop: "1.25rem" }}>
      <legend style={{ color: result.ok ? "var(--success)" : "var(--danger)" }}>
        {result.ok ? "✓ Test passed" : "✗ Test failed"} — {result.duration_ms} ms
      </legend>

      {result.summary && (
        <div style={{ marginBottom: "1rem" }}>
          <h4 style={{ fontSize: "0.72rem", textTransform: "uppercase", color: "var(--text-muted)", margin: "0 0 0.4rem" }}>Summary</h4>
          <pre style={preStyle}>{JSON.stringify(result.summary, null, 2)}</pre>
        </div>
      )}

      {result.error && (
        <div style={{ marginBottom: "1rem", padding: "0.6rem 0.85rem", background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: 6, fontSize: "0.85rem", color: "var(--danger)" }}>
          <strong>{result.error.code || "error"}:</strong> {result.error.message}
          {result.error.missing && (
            <ul style={{ marginTop: "0.4rem", marginBottom: 0 }}>
              {result.error.missing.map((m, i) => <li key={i}>{m.service}.{m.field}</li>)}
            </ul>
          )}
        </div>
      )}

      {result.trace?.length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          <h4 style={{ fontSize: "0.72rem", textTransform: "uppercase", color: "var(--text-muted)", margin: "0 0 0.4rem" }}>
            Provider API calls ({result.trace.length})
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", fontSize: "0.78rem" }}>
            {result.trace.map((c, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "60px 50px 1fr auto",
                gap: "0.4rem", padding: "0.35rem 0.6rem",
                background: "var(--bg-subtle)", border: "1px solid var(--border)",
                borderRadius: 4, fontFamily: "monospace",
              }}>
                <span style={{ color: c.provider === "igt" ? "#a855f7" : "#3b82f6", fontWeight: 600 }}>{c.provider}</span>
                <span style={{ color: c.ok ? "var(--success)" : "var(--danger)" }}>{c.status}</span>
                <span style={{ wordBreak: "break-all" }}>{c.method} {c.path}</span>
                <span style={{ color: "var(--text-muted)" }}>{c.duration_ms} ms</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.result && (
        <details>
          <summary style={{ cursor: "pointer", fontSize: "0.8rem", color: "var(--text-muted)" }}>Full result body</summary>
          <pre style={{ ...preStyle, marginTop: "0.4rem" }}>{JSON.stringify(result.result, null, 2)}</pre>
        </details>
      )}
    </fieldset>
  );
}

const preStyle = {
  margin: 0, padding: "0.6rem 0.75rem",
  background: "var(--bg-elevated)", border: "1px solid var(--border)",
  borderRadius: 6, fontSize: "0.75rem", overflow: "auto", maxHeight: 300,
};

/* ── Purge test data — voids sandbox passes + deletes is_test rows ── */
function PurgeButton({ venueId, onPurged }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  async function openModal() {
    setOpen(true); setErr(""); setResult(null);
    try { setSummary(await adminApi.itPurgeSummary(venueId)); }
    catch (e) { setErr(e.message); }
  }

  async function purge() {
    setBusy(true); setErr("");
    try { setResult(await adminApi.itPurge(venueId)); if (onPurged) await onPurged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <>
      <button className="btn btn-outline" onClick={openModal}>Purge test data</button>
      {open && (
        <div className="modal-overlay" onClick={() => !busy && setOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-text">
                <h2>Purge test data</h2>
                <p>Voids sandbox Litecard passes and deletes every <code>is_test=true</code> row for this venue.</p>
              </div>
            </div>
            <div className="modal-body">
              {err && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{err}</div>}

              {!result && summary && (
                <>
                  <div style={{ marginBottom: "1rem" }}>
                    <div>Will delete:</div>
                    <ul style={{ marginTop: 4 }}>
                      <li><strong>{summary.members}</strong> test member{summary.members === 1 ? "" : "s"}</li>
                      <li><strong>{summary.passes}</strong> test digital pass{summary.passes === 1 ? "" : "es"}</li>
                      <li><strong>{summary.join_logs}</strong> test activity log row{summary.join_logs === 1 ? "" : "s"}</li>
                    </ul>
                  </div>
                  <div style={{ padding: "0.7rem 0.9rem", background: "var(--warning-soft)", border: "1px solid var(--warning)", borderRadius: 6, fontSize: "0.85rem" }}>
                    Sandbox Litecard cards will be marked VOIDED. Sandbox Stripe charges (if any) are left alone — they expire on Stripe's side.
                  </div>
                </>
              )}

              {result && (
                <div>
                  <div className="toast toast-success" style={{ marginBottom: "1rem" }}>Purge complete.</div>
                  <pre style={preStyle}>{JSON.stringify(result, null, 2)}</pre>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setOpen(false)} disabled={busy}>Close</button>
              {!result && <button className="btn btn-primary" disabled={busy || !summary} onClick={purge}>{busy ? "Purging…" : "Purge"}</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
